import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { config } from '@uhg-abyss/web/tools/config';
import { useSearchParams } from '@uhg-abyss/web/tools/reactRouterTools';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import { useGuestStore } from 'parcels/src/store/useGuestStore';
import { GuestState } from 'parcels/src/store/useGuestStore/guestStore';
import React, { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Constants } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { StyledDiv } from '../../common/ConstantsStyles';
import { getFeatureFlag } from '../../common/Utils';
import { useFpcGuestToken } from '../../hooks/useFpcGuestToken';
import { useGeoLocationStorage } from '../../hooks/useGeoLocationStorage';
import { useLagoon } from '../../hooks/useLagoon';
import { useUserLocation } from '../../hooks/useUserLocation';
import {
  getChipValue,
  getGuestPlanSelectionData,
  getLobFromPortalLOB,
  handleGuestPlanNavigation,
  toSlug,
} from './Guest.utils';
import { isLawwGuest } from './utils';

const FPC_BASE_URL = config('FPC_BASE_URL');

const YearCardStyledCard = styled(Card, {
  '&.abyss-card-root': {
    width: '342px',
    height: '210px',
    borderRadius: '8px',
    padding: '0px',
    marginTop: '0px !important',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },
  },
});

const YearCard = (
  title: string,
  description: string,
  icon: string,
  buttonText: string,
  coverageType: string,
  toastMessageForFpcTokenError: any,
  planSelectionLob: string,
  chipValueParam: string
) => {
  const chipValue = getChipValue(coverageType);
  const { navigate } = useRouter();
  const { toast } = useToast();

  const { guestPlanSelectorData, setGuestPlanSelectorData } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestPlanSelectorData: state.guestPlanSelectorData,
      setGuestPlanSelectorData: state.setGuestPlanSelectorData,
    }))
  );

  const { selectedPlan, lineOfBusiness, stateCode, planCategory } =
    getGuestPlanSelectionData();
  // No plan has been chosen yet: year is being selected before location/plan (year-first flow).
  const isYearFirstMode = !selectedPlan;
  const lob = isYearFirstMode
    ? planSelectionLob
    : getLobFromPortalLOB(lineOfBusiness);

  const planSelectionConfig = useLagoon('config')();

  const featureFlags = useLagoon('feature-flags')();
  const isGroupDrivenEnabled = getFeatureFlag(
    featureFlags || [],
    ConstantsLagoon.GUEST_PLAN_SELECTION.ENABLE_GROUP_DRIVEN
  );

  const signInUrl = planSelectionConfig?.find(
    (row: any) => row.key === `SIGNIN_URL_${lob?.toUpperCase()}`
  )?.value;
  const {
    zipCode: zipcode,
    longitude,
    latitude,
  } = useGeoLocationStorage() || {};
  const [userLocation, getUserLocation] = useUserLocation({});

  const [, getFpcGuestToken] = useFpcGuestToken({});
  const fipscode = stateCode || sessionStorage.getItem('countyFipsCode') || '';

  const handleCardClick = () => {
    if (isYearFirstMode) {
      // Persist the chosen year and continue on to location/plan selection.
      setGuestPlanSelectorData({
        ...guestPlanSelectorData,
        planYear: title,
        coverageType,
        isYearFirstFlow: true,
      });
      navigate(
        `/plan-selection?planSelectionLob=${planSelectionLob}&coverageType=${coverageType}&chipValue=${
          chipValueParam || chipValue
        }`
      );
      return;
    }

    handleGuestPlanNavigation({
      getFpcGuestToken,
      selectedPlan,
      planYear: title,
      fipscode,
      FPC_BASE_URL: FPC_BASE_URL || '',
      zipcode,
      chipValue,
      coverageType,
      navigate,
      lob,
      stateCode,
      longitude,
      latitude,
      signInUrl,
      toast,
      toastMessageForFpcTokenError,
      isDual: true,
      planCategory,
      guestPlanSelectorStoreData: guestPlanSelectorData,
      isGroupDrivenEnabled,
      allConfigs: planSelectionConfig,
      getUserLocation,
      userLocation,
    });
  };

  return (
    <YearCardStyledCard
      key={`${title}-${coverageType}`}
      onClick={handleCardClick}
    >
      <StyledDiv
        css={{
          padding: '0px',
          display: 'flex',
          flexDirection: 'column',
          height: '154px',
        }}
      >
        <Layout.Stack
          css={{
            'abyss-layout-stack': {
              padding: '16px',
              paddingBottom: '16px',
              alignItems: 'start',
              marginTop: '0px',
              marginBottom: '0px',
            },
            'abyss-layout-stack > :first-of-type': {
              margin: '0',
              height: '48px',
              width: '48px',
            },
          }}
        >
          <IconBrand icon={icon} size="48px" />
          <Heading css={{ paddingTop: '16px' }} level={6} size="xs">
            {title}
          </Heading>
          <Text
            css={{
              'abyss-text-root': {
                display: 'inline-block',
                lineHeight: '1.3 !important',
              },
            }}
            fontSize="sm"
          >
            {description}
          </Text>
        </Layout.Stack>
      </StyledDiv>
      <StyledDiv
        css={{
          padding: '16px',
          height: '52px',
          width: '342px',
          marginLeft: '0px',
          marginRight: '0px',
          borderTop: '1px solid $greyBorderColor',
        }}
      >
        <Layout.Group>
          <Link
            afterIcon="chevron_right"
            data-testid={`select-${toSlug(title)}-link`} // Use the utility function
            isStandardAnchor
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick();
            }}
            size={'$sm'}
          >
            {buttonText}
          </Link>
        </Layout.Group>
      </StyledDiv>
    </YearCardStyledCard>
  );
};

export const GuestPlanYearSelection = () => {
  const [searchParams] = useSearchParams();
  const coverageType = searchParams?.get('coverageType') || '';
  const planSelectionLob = searchParams?.get('planSelectionLob') || '';
  const chipValueParam = searchParams?.get('chipValue') || '';

  const lagoonUIMessage = useLagoon('ui-messaging')();
  const currentYear = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.YEAR_SELECTION_CURRENT_YEAR,
  })?.message;
  const futureYear = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.YEAR_SELECTION_FUTURE_YEAR,
  })?.message;
  const yearSelectionDescription = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.YEAR_SELECTION_DESCRIPTION,
  })?.message;
  const yearSelectionButtonText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.YEAR_SELECTION_BUTTON_TEXT,
  })?.message;

  const chooseYearText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CHOOSE_A_YEAR,
  })?.message;
  const chooseYearHeadingText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CHOOSE_A_YEAR_HEADING,
  })?.message;
  const signInMessageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN_MESSAGE,
  })?.message;
  const signInText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN,
  })?.message;

  // Retrieve the sign-in URL saved by GuestHeader
  const savedSignInUrl = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.GUEST_SIGN_IN_URL
  );
  // LAWW guests must sign in via the LAWW portal (/en/public), not the saved UHC URL.
  const lawwSignInUrl = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SIGN_IN_ALERT_URL,
  })?.url;
  const signInUrl = isLawwGuest()
    ? lawwSignInUrl || savedSignInUrl
    : savedSignInUrl;

  const toastMessageForFpcTokenError = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.FPC_ERROR_POPUP_MESSAGE,
  })?.message;

  const { setChooseType, guestPlanSelectorData, setGuestPlanSelectorData } =
    useGuestStore(
      useShallow((state: GuestState) => ({
        setChooseType: state.setChooseType,
        guestPlanSelectorData: state.guestPlanSelectorData,
        setGuestPlanSelectorData: state.setGuestPlanSelectorData,
      }))
    );
  useEffect(() => {
    if (chooseYearText) {
      setChooseType(chooseYearText);
    }
  }, [chooseYearText, setChooseType]);

  useEffect(() => {
    // Landing on Year selection with a previously chosen plan/location (e.g. via the
    // breadcrumb) means the year is changing, so clear state that no longer applies.
    const { selectedPlan } = getGuestPlanSelectionData();
    if (selectedPlan) {
      sessionStorage.removeItem(
        Constants.STORAGE_KEYS.SESSION.GUEST_PLAN_SELECTION_DATA
      );
      sessionStorage.removeItem(Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION);
      sessionStorage.removeItem(Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS);
      setGuestPlanSelectorData({
        ...guestPlanSelectorData,
        planName: undefined,
        policyNumber: undefined,
        delsysCode: undefined,
        stateCode: undefined,
        isYearFirstFlow: true,
      });
    }
  }, []);

  return (
    <React.Fragment>
      <Heading
        css={{ marginTop: '32px' }}
        data-testid="year-selection"
        level={4}
        size="sm"
        textAlign="center"
      >
        {chooseYearHeadingText}
      </Heading>

      <Flex
        alignItems="baseline"
        css={{ marginTop: '8px' }}
        direction="row"
        justify="center"
      >
        <Text>{signInMessageText}</Text>
        <Link
          data-testid={`sign-in-link`}
          href={signInUrl}
          openNewWindow={false}
          style={{ marginLeft: '4px' }}
        >
          {signInText}
        </Link>
      </Flex>

      <Flex
        alignItems="baseline"
        direction="row"
        justify="center"
        style={{ gap: '24px', marginTop: '32px' }}
      >
        {YearCard(
          currentYear,
          `${yearSelectionDescription} ${currentYear}`,
          'calendar',
          `${yearSelectionButtonText} ${currentYear}`,
          coverageType,
          toastMessageForFpcTokenError,
          planSelectionLob,
          chipValueParam
        )}
        {YearCard(
          futureYear,
          `${yearSelectionDescription} ${futureYear}`,
          'calendar',
          `${yearSelectionButtonText} ${futureYear}`,
          coverageType,
          toastMessageForFpcTokenError,
          planSelectionLob,
          chipValueParam
        )}
      </Flex>
    </React.Fragment>
  );
};
